// src/components/competitions/SubmissionCard.jsx
import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import FireworksVoteButton from "@/components/ui/FireworksVoteButton";
import MediaPreview from "@/components/ui/MediaPreview";
import ZeloScore from "@/components/ui/ZeloScore";

const SubmissionCard = ({
  submission,
  isLiked,
  votes,
  authorName,
  isLive,
  onLike,
  onClick,
  competitionType,           // ← NEW: drives card-level UI branching
}) => {
  // 🧠 Access current logged-in user to enable live-sync of author name
  const { user: authUser } = useAuth();

  // Is this a Community Score competition?
  const isCommunityScore = competitionType === "community_score";

  // 🪄 Live-sync display name if submission belongs to current user
  const effectiveAuthorName =
    (submission.user?.id ?? submission.user) === authUser?.id
      ? authUser?.profile?.name ||
        authUser?.first_name ||
        authUser?.username ||
        authorName
      : authorName;

  return (
    <Card
      variant="dark"
      className="group flex flex-col rounded-xl shadow-lg bg-stone-brown
                 transition-all duration-300 ease-out
                 hover:shadow-[0_12px_40px_-24px_rgba(245,197,24,0.22)]
                 hover:scale-[1.02] hover:-translate-y-1
                 cursor-pointer border-stone-brown
                 relative overflow-hidden
                 before:pointer-events-none before:absolute before:inset-0 before:bg-gradient-to-br 
                 before:from-mikado-yellow/5 before:to-transparent before:opacity-0
                 before:transition-opacity before:duration-300 hover:before:opacity-60"
      onClick={onClick}
    >
      {/* Decorative shimmer effect on hover */}
      <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
        <div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer"
          style={{ backgroundSize: "200% 100%" }}
        />
      </div>

      {/* Thumbnail */}
      <div className="relative aspect-[4/3] overflow-hidden rounded-t-xl">
        <MediaPreview
          file={submission.files?.[0]}
          thumbnail
          className="absolute inset-0 w-full h-full object-cover 
                     transition-transform duration-500 ease-out
                     group-hover:scale-110"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-stone-brown/80 via-stone-brown/20 to-transparent 
                        opacity-60 group-hover:opacity-40 transition-opacity duration-300" />
        <div className="pointer-events-none absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-mikado-yellow/20 to-transparent 
                        opacity-0 group-hover:opacity-60 transition-opacity duration-300 blur-2xl" />
      </div>

      {/* Content (~30% of card height) */}
      <CardContent
        className="relative h-[90px] px-3 pr-20 py-4
             z-10 flex flex-col justify-start gap-1 overflow-hidden"
      >
        {/* Author name with underline animation */}
        <div className="text-[14px] leading-tight">
          <span
            className="inline-flex items-center gap-2 font-semibold text-imperial-red 
                       relative
                       after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 
                       after:bg-imperial-red after:transition-all after:duration-300
                       group-hover:after:w-full"
            title={effectiveAuthorName || ''}
          >
            {effectiveAuthorName}
          </span>
        </div>

        {/* Description with ellipsis after 2 lines */}
        <p
          className="text-[13px] text-swan font-poppins transition-colors duration-300 group-hover:text-white"
          style={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
            lineHeight: "1.25rem",
          }}
        >
          {submission.description}
        </p>

        {/* ─────────────────────────────────────────────────────────────
            Bottom-right corner:
            - Community Score → read-only ZeloScore pill (engagement happens
              inside the modal; the card is a teaser, not a control panel)
            - Voting          → interactive FireworksVoteButton (unchanged)
           ───────────────────────────────────────────────────────────── */}
        {isCommunityScore ? (
          <div className="absolute bottom-2 right-3 sm:bottom-4 sm:right-3">
            <ZeloScore score={submission.zelo_score} size="card" />
          </div>
        ) : (
          <div className="absolute bottom-2 right-4 sm:bottom-4 sm:right-3">
            <div
              className={`pointer-events-none absolute inset-0 rounded-full blur-2xl transition-opacity duration-300
                          ${isLiked ? "opacity-45 bg-imperial-red/35" : "opacity-25 bg-imperial-red/20"}`}
            />
            <div className="relative leading-none group/rocket cursor-pointer">
              <span className="pointer-events-none absolute inset-0 rounded-full bg-imperial-red/30
                               opacity-0 group-hover/rocket:opacity-60 animate-ping" />
              <FireworksVoteButton
                isLiked={isLiked}
                votes={votes}
                onLike={isLive ? onLike : null}
                disabled={!isLive}
                className="relative z-10 transition-transform duration-200 ease-out
                           transform scale-[2.8] sm:scale-[3.1]
                           group-hover/rocket:scale-[2.9] group-hover/rocket:-translate-y-0.5
                           group-hover/rocket:drop-shadow-[0_10px_18px_rgba(220,38,38,0.55)] leading-none"
              />
            </div>
          </div>
        )}
      </CardContent>

      {/* Bottom accent line on hover */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-mikado-yellow/40 to-transparent
                      opacity-0 group-hover:opacity-90 transition-opacity duration-300" />
    </Card>
  );
};

export default SubmissionCard;